import React, {useRef} from 'react';
import {ScrollView, View} from 'react-native';
import {useSelector} from 'react-redux';
import {noop} from 'lodash';
import {get} from 'lodash';

import LastChangeDateComponent from '/component/partial/LastChangeDateComponent';
import {Alerts} from '/component/screen/ClientProfile/Alerts';
import {ClientJourney} from '/component/screen/ClientProfile/ClientJourney';
import Contact from '/component/screen/ClientProfile/Contact/';
import CreditsAndRewards from '/component/screen/ClientProfile/CreditsAndRewards';
import DateJoined from '/component/screen/ClientProfile/DateJoined';
import NextPremierOrder from '/component/screen/ClientProfile/NextPremierOrder';
import Notes from '/component/screen/ClientProfile/Notes';
import OrderHistory from '/component/screen/ClientProfile/OrderHistory';
import {ORDER_DETAILS} from '/enum/records.enum';
import {COACH_PROFILE} from '/enum/recordType.enum';
import {EVENT_NAMES, SCREEN_NAMES, USER_TYPES} from '/enum/tealium.enum';
import {variablesFeatureFlag} from '/redux/selector/remoteConfig.selector';
import {generateCallback} from '/util/component.util';
import {getDateUtilDefaultInstance} from '/util/date.util';
import {shouldReleaseFeature} from '/util/featureRelease.util';
import {getTealiumUtilDefaultInstance} from '/util/tealium';

import {styles} from './ClientInformation.style';
interface FeatureFlag {
  showCoachAndClientNotes?: boolean;
  showClientJourney?: boolean;
  showClientAlerts?: boolean;
  popoverShow?: any;
}

interface ClientInformationProps {
  onShowRecord: (params: {recordType: string; recordId: string}) => void;
  featureFlag: FeatureFlag;
  navigate: (path: string) => void;
  activationDate: Date;
  firstOrderId: string;
  recordId: string;
  coachId: string;
  coachName: string;
  userId: string;
  push: (path: string, params?: any) => void;
  popoverShow: any;
  lastPaidRank: string;
}
export const ClientInformation = (props: ClientInformationProps) => {
  const featureFlag = useSelector(variablesFeatureFlag);
  const removeExpiringRewards = shouldReleaseFeature(
    get(featureFlag, 'ff_Remove_Expiring_Rewards', ''),
  );
  const {
    onShowRecord,
    featureFlag: remoteFeatureFlag = {
      showCoachAndClientNotes: false,
      showClientJourney: false,
      showClientAlerts: false,
    },
    navigate = noop,
    activationDate,
    firstOrderId,
    recordId,
    coachId,
    coachName,
    userId,
    push = noop,
    popoverShow,
    lastPaidRank,
  } = props;
  const onPageLoadSendEvent =
    getTealiumUtilDefaultInstance().generateTrackEvent({
      eventName: EVENT_NAMES.onPageLoad,
    });

  const handleViewMoreOrderHistoryRef = useRef(() => {
    navigate('client-profile-order-history-list-screen');
    onPageLoadSendEvent({
      currentScreenName: SCREEN_NAMES.orderHistoryList,
      previousScreenName: SCREEN_NAMES.clientProfile,
      lastPaidRank: lastPaidRank,
    });
  });

  const handleOrderPressRef = useRef(({orderId}: any) => {
    push('client-profile-order-detail-screen', {
      orderId,
      modalName: ORDER_DETAILS,
    });
  });

  const handleTaskPressRef = useRef(
    ({taskItem, taskChecked, onTaskStatusChange}: any) => {
      navigate('client-journey-task-details-screen', {
        taskItem,
        taskChecked,
        onTaskStatusChange,
      });
    },
  );
  const profileClickSendEvent =
    getTealiumUtilDefaultInstance().generateTrackEvent({
      eventName: EVENT_NAMES.profileClick,
    });
  const generateOnViewProfilePress = generateCallback({
    func: profileClickSendEvent,
    params: {
      userType: USER_TYPES.client,
      userId: coachId,
      userFullName: coachName,
      lastPaidRank: lastPaidRank,
    },
  });
  const isFrontlineClient = coachId === userId;
  const isRecentClient =
    getDateUtilDefaultInstance().getDaysToToday(activationDate) < 365;

  const showClientJourney =
    remoteFeatureFlag.showClientJourney && isFrontlineClient && isRecentClient;
  return (
    <ScrollView style={styles.content}>
      <DateJoined
        clientId={recordId}
        onCoachProfilePress={() => {
          onShowRecord({
            recordType: COACH_PROFILE,
            recordId: coachId,
          });
          generateOnViewProfilePress();
        }}
      />
      <Contact clientId={recordId} />
      {remoteFeatureFlag.showCoachAndClientNotes && (
        <Notes
          clientId={recordId}
          disabled={true}
          greyOutTextInputValue={false}
          onPress={() => navigate('client-notes-edit-screen')}
          FooterComponent={LastChangeDateComponent}
          showEditNotesLabel={true}
        />
      )}
      {remoteFeatureFlag.showClientAlerts && <Alerts clientId={recordId} />}
      {showClientJourney && (
        <ClientJourney
          clientId={recordId}
          onOrderPress={handleOrderPressRef.current}
          onShowTaskDetail={handleTaskPressRef.current}
          orderId={firstOrderId}
          popoverShow={popoverShow}
        />
      )}
      <NextPremierOrder clientId={recordId} isClientProfile />
      {!removeExpiringRewards && <CreditsAndRewards clientId={recordId} />}
      <OrderHistory
        clientId={recordId}
        onOrderPress={handleOrderPressRef.current}
        onViewMore={handleViewMoreOrderHistoryRef.current}
      />
      <View style={styles.sections} />
    </ScrollView>
  );
};
export default ClientInformation;
